import get from 'lodash.get';
import React, { Component } from 'react';
import { bool, object, string } from 'prop-types';
import { createRefetchContainer, graphql } from 'react-relay';

import Dropdown, { alignConstants } from '../Dropdown/Dropdown';
import DropdownLoadingContent from '../Dropdown/DropdownLoadingContent';
import LikesIcon from './LikesIcon';

import {
    MAX_TILES,
    MAX_TILE_ITEMS,
    ITEM_EVENTS_SHOWN_PERIODE_MS,
} from '../FavNotifications/favNotificationsConstants';
import { itemEventsShownMutation } from '../FavNotifications/itemEventsShownMutation';

const hasNewEvents = viewer => get(viewer, 'favoriteNotifications.hasNewEvents');
const getCreatedAfterDate = () =>
    new Date(new Date().getTime() - ITEM_EVENTS_SHOWN_PERIODE_MS).toJSON();

class LikesLink extends Component {
    constructor(props) {
        super(props);

        this.state = {
            FavNotifications: null,
            fragmentVariables: {
                eventsLimit: MAX_TILES,
                fetchNotifications: false,
                createdAfter: getCreatedAfterDate(),
                notificationItemsLimit: MAX_TILE_ITEMS,
            },
        };

        this.triggerNotificationsFetch = this.triggerNotificationsFetch.bind(this);
        this.loadComponent = this.loadComponent.bind(this);
    }

    componentDidUpdate(prevProps) {
        if (this.props.fetchNewEvents && !prevProps.fetchNewEvents) {
            const { fetchNewEvents, userId } = this.props;
            this.fetchNewEvents({ fetchNewEvents, userId });
        }
    }

    fetchNewEvents(variables) {
        this.setState(
            {
                fragmentVariables: {
                    ...this.state.fragmentVariables,
                    ...variables,
                },
            },
            () => {
                this.props.relay.refetch(this.state.fragmentVariables);
            }
        );
    }

    fetchNotifications() {
        if (!this.state.fragmentVariables.fetchNotifications) {
            this.setState(
                {
                    fragmentVariables: {
                        ...this.state.fragmentVariables,
                        fetchNotifications: true,
                        notificationItemsLimit: MAX_TILE_ITEMS,
                    },
                },
                () => {
                    this.props.relay.refetch(this.state.fragmentVariables, null, () => {
                        this.onNotificationsShown();
                    });
                }
            );
        }
    }

    onNotificationsShown() {
        const { viewer, userId, relay } = this.props;

        if (hasNewEvents(viewer)) {
            const { environment } = relay;
            const { favoriteNotifications } = viewer;
            const { id } = favoriteNotifications;

            itemEventsShownMutation({
                environment,
                id,
                userId,
            });
        }
    }

    triggerNotificationsFetch() {
        const { hasUserId, hasRootPendingRefetch } = this.props;

        if (hasUserId && !hasRootPendingRefetch) {
            this.fetchNotifications();
        }
    }

    loadComponent() {
        import(/* webpackChunkName: "FavNotifications" */
        '../FavNotifications/FavNotifications').then(({ default: FavNotifications }) => {
            this.setState({ FavNotifications });
        });
    }

    render() {
        const { favoritesUrl, isClient, hasUserId, viewer } = this.props;
        const { favoriteNotificationsTiles = null } = viewer;
        const { FavNotifications } = this.state;

        return isClient ? (
            <Dropdown
                align={alignConstants.ALIGN_RIGHT}
                onShow={this.loadComponent}
                hasDropdownHeader
            >
                <LikesIcon
                    hasUnread={hasNewEvents(viewer)}
                    favoritesUrl={favoritesUrl}
                    onMouseEnter={this.triggerNotificationsFetch}
                />
                {!!FavNotifications ? (
                    <FavNotifications
                        hasUserId={hasUserId}
                        hasNewEvents={hasNewEvents(viewer)}
                        favoriteNotificationsTiles={favoriteNotificationsTiles}
                        favoritesUrl={favoritesUrl}
                        viewer={viewer}
                    />
                ) : (
                    <DropdownLoadingContent />
                )}
            </Dropdown>
        ) : (
            <LikesIcon favoritesUrl={favoritesUrl} />
        );
    }
}

LikesLink.propTypes = {
    fetchNewEvents: bool,
    favoritesUrl: string,
    hasRootPendingRefetch: bool,
    hasUserId: bool,
    isClient: bool,
    relay: object,
    userId: string,
    viewer: object,
};

LikesLink.defaultProps = {
    favoritesUrl: '/favorites/items/',
};

LikesLink = createRefetchContainer(
    LikesLink,
    {
        viewer: graphql`
            fragment Likes_viewer on Viewer
                @argumentDefinitions(
                    createdAfter: { type: "String!", defaultValue: "" }
                    eventsLimit: { type: "Int!", defaultValue: 0 }
                    fetchNewEvents: { type: "Boolean!", defaultValue: false }
                    fetchNotifications: { type: "Boolean!", defaultValue: false }
                    notificationItemsLimit: { type: "Int!", defaultValue: 0 }
                ) {
                favoriteNotifications(userId: $userId) @include(if: $fetchNewEvents) {
                    id
                    hasNewEvents(eventsLimit: $eventsLimit, createdAfter: $createdAfter)
                }
                favoriteNotificationsTiles: favoriteNotifications(userId: $userId)
                    @include(if: $fetchNotifications) {
                    ...FavNotifications_favoriteNotificationsTiles
                        @arguments(
                            createdAfter: $createdAfter
                            eventsLimit: $eventsLimit
                            notificationItemsLimit: $notificationItemsLimit
                        )
                }
            }
        `,
    },
    graphql`
        query LikesRefetchQuery(
            $createdAfter: String!
            $eventsLimit: Int!
            $fetchNewEvents: Boolean!
            $fetchNotifications: Boolean!
            $notificationItemsLimit: Int!
            $userId: String!
        ) {
            viewer {
                ...Likes_viewer
                    @arguments(
                        createdAfter: $createdAfter
                        eventsLimit: $eventsLimit
                        fetchNewEvents: $fetchNewEvents
                        fetchNotifications: $fetchNotifications
                        notificationItemsLimit: $notificationItemsLimit
                    )
            }
        }
    `
);

export default LikesLink;
